import * as React from 'react';
import { ChangeEvent, memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Button, Step, StepContent, StepLabel, Stepper, Typography } from '@mui/material';

import { TwoFactorAuthentication } from '@/legacy/features/user/twoFactorAuthentication/twoFactorAuthentication';
import { getFieldError } from '@/legacy/utils/reactHelpers';

import Code from './CodeFragment';
import DisableVerification from './DisableVerificationFragment';
import { VerificationStep } from './helper';
import SmsPhoneFragment from './SmsPhoneFragment';
import { State } from './TwoFactorSetupDialog.reducer';
import UserOtpQrCodeFragment from './UserOtpQrCodeFragment';
import VerificationProvider from './VerificationProviderFragment';

interface Props {
  twoFactorAuthenticationDisabler: TwoFactorAuthentication;
  twoFactorAuthenticationDisablers: Array<TwoFactorAuthentication>;
  state: State;
  onCodeChange: { (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void };
  onNextStep: { (): void };
  onPhoneNumberChange: { (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void };
  onPreviousStep: { (): void };
  onSmsCountryChange: { (e: ChangeEvent<HTMLInputElement>): void };
  onVerificationChange: { (e: ChangeEvent<HTMLInputElement>): void };
  onUnableToScan: { (): void };
  onTwoFactorAuthenticationDisablerChange: (verification: TwoFactorAuthentication) => void;
  onResendCode: () => void;
  onCallMe: () => void;
}

const TwoFactorStepper = (props: Props): React.JSX.Element => {
  const { formatMessage } = useIntl();

  const {
    twoFactorAuthenticationDisabler,
    twoFactorAuthenticationDisablers,
    state,
    onCodeChange,
    onNextStep,
    onPhoneNumberChange,
    onPreviousStep,
    onSmsCountryChange,
    onVerificationChange,
    onUnableToScan,
    onTwoFactorAuthenticationDisablerChange,
    onResendCode,
    onCallMe,
  } = props;

  const {
    code,
    fieldErrors,
    isEnabled,
    isLoading,
    isUnableToScan,
    otpSecretKey,
    phoneNumber,
    smsCountry,
    steps,
    verificationMethod,
    otpAccountName,
    twoFactorAuthentication,
    isMaxNumberOfAuthMethodsReachedForCurrentType,
  } = state;

  const currentStep = state.verificationStep;

  const getStepContent = (step: VerificationStep): React.JSX.Element => {
    switch (step) {
      case VerificationStep.SelectVerificationProvider:
        return (
          <VerificationProvider
            isLoading={isLoading}
            onVerificationMethodChange={onVerificationChange}
            verificationMethod={verificationMethod}
            isMaxNumberOfAuthMethodsReachedForCurrentType={
              isMaxNumberOfAuthMethodsReachedForCurrentType
            }
          />
        );

      case VerificationStep.EnterPhoneNumber:
        return (
          <SmsPhoneFragment
            isLoading={isLoading}
            onPhoneNumberChange={onPhoneNumberChange}
            onSmsCountryChange={onSmsCountryChange}
            phoneNumber={phoneNumber}
            phoneNumberErrorMessage={getFieldError('phoneNumber', fieldErrors)}
            smsCountry={smsCountry}
          />
        );

      case VerificationStep.EnterSmsCode:
      case VerificationStep.ConfirmOtpCode:
      case VerificationStep.EnterDisableSmsCode:
      case VerificationStep.ConfirmDisableOtpCode:
        return (
          <Code
            code={code}
            fieldErrors={fieldErrors}
            isEnabled={isEnabled}
            isLoading={isLoading}
            onCodeChange={onCodeChange}
            onResendCode={onResendCode}
            onCallMe={onCallMe}
            phoneNumber={phoneNumber}
            verificationMethod={verificationMethod}
          />
        );

      case VerificationStep.OtpQrCode:
        return (
          <UserOtpQrCodeFragment
            isUnableToScan={isUnableToScan}
            onUnableToScan={onUnableToScan}
            otpSecretKey={otpSecretKey}
            otpAccountName={otpAccountName}
          />
        );

      case VerificationStep.SelectDisabler:
        return (
          <DisableVerification
            errorMessage={getFieldError('disableVerification', fieldErrors)}
            isLoading={isLoading}
            onTwoFactorAuthenticationDisablerChange={onTwoFactorAuthenticationDisablerChange}
            twoFactorAuthenticationDisabler={twoFactorAuthenticationDisabler}
            twoFactorAuthenticationDisablers={twoFactorAuthenticationDisablers}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Stepper activeStep={currentStep} orientation="vertical">
      {steps.map(({ step, title, blockBack }, stepIndex) => (
        <Step key={step}>
          <StepLabel>{title}</StepLabel>

          <StepContent>
            <Typography>{getStepContent(step)}</Typography>
            <div>
              <div>
                {currentStep !== 0 && (
                  <Box display="inline-block" mr={1}>
                    <Button
                      variant="text"
                      onClick={onPreviousStep}
                      disabled={isLoading || blockBack}
                    >
                      <FormattedMessage defaultMessage="Back" />
                    </Button>
                  </Box>
                )}

                <Button
                  color="primary"
                  onClick={onNextStep}
                  disabled={
                    isLoading ||
                    (!twoFactorAuthentication && isMaxNumberOfAuthMethodsReachedForCurrentType)
                  }
                >
                  {stepIndex === steps.length - 1
                    ? formatMessage({ defaultMessage: 'Finish' })
                    : formatMessage({ defaultMessage: 'Continue' })}
                </Button>
              </div>
            </div>
          </StepContent>
        </Step>
      ))}
    </Stepper>
  );
};

export default memo(TwoFactorStepper);
